/** Metadata models (definitions stored separately from values), field permissions and server-side validation. */
import { z } from 'zod';
import { ROLE_KEY } from './roles.ts';
import { normalizeDateTime } from './date-schedule.ts';
import { RIGHTS_ROLES, RIGHTS_STATUSES, RIGHTS_STATUS_LABELS, type RightsRole, type RightsValues } from './rights.ts';

/** `datetime` (1.0.0-rc.10): a date and a time, stored as a UTC instant `YYYY-MM-DDTHH:MM:SSZ`. */
export const FieldType = z.enum(['text', 'rich_text', 'number', 'boolean', 'date', 'datetime', 'timecode', 'vocabulary', 'tags']);
export type FieldType = z.infer<typeof FieldType>;

export const FieldDefinitionSchema = z.object({
  /** Stable id. Values are keyed by id so relabelling never breaks stored data. */
  id: z.string().regex(/^f_[a-z0-9_]+$/),
  label: z.string(),
  type: FieldType,
  required: z.boolean().default(false),
  help: z.string().optional(),
  maxLength: z.number().int().positive().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  multiple: z.boolean().optional(),
  section: z.string().optional(),
  facet: z.boolean().optional(),
  column: z.boolean().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  default: z.unknown().optional(),
  /**
   * Show the field only when another field has one of these values (`field` + `equals`), and/or only for assets in
   * one of these classes (`classes`, category ids). Both conditions are optional but at least one is needed; with
   * both, both must hold. Stored models from before classes existed carry only `field` and `equals`.
   */
  visibleWhen: z.object({
    field: z.string().optional(),
    equals: z.array(z.union([z.string(), z.boolean()])).optional(),
    classes: z.array(z.string()).optional(),
  }).optional(),
  /** Warn when this field is empty (the asset counts as having incomplete metadata); unlike `required` it never blocks saving. Required wins. */
  needed: z.boolean().optional(),
  /** Show this field in the Library inspector. When no field is flagged the inspector shows every field that has a value. */
  inspector: z.boolean().optional(),
  /** Hidden from forms, facets, columns and search for everyone; stored values are kept. */
  hidden: z.boolean().optional(),
  /** Roles allowed to see / change the value. Omitted means everyone who can read / edit assets. */
  viewRoles: z.array(z.string().regex(ROLE_KEY)).optional(),
  editRoles: z.array(z.string().regex(ROLE_KEY)).optional(),
  /**
   * Timecode fields that mark where the programme starts and ends: shown on the player's timeline, and exports can keep
   * only that part. 1.8.0: the rights roles (rights.ts RIGHTS_ROLES), read by rightsValues; at most one field per role
   * (rightsRoleProblems).
   */
  role: z.enum(['programme_in', 'programme_out', ...RIGHTS_ROLES]).optional(),
});
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;

/** The programme's in and out timecodes from an asset's metadata, when the model marks fields for them. */
export function programmeMarks(def: { fields: FieldDefinition[] } | null | undefined, values: Record<string, unknown>): { in: string | null; out: string | null } {
  const pick = (role: string) => {
    const f = def?.fields.find((x) => x.role === role && x.type === 'timecode');
    const v = f ? values[f.id] : null;
    return typeof v === 'string' && v ? v : null;
  };
  return { in: pick('programme_in'), out: pick('programme_out') };
}

/* ---------------------------------------------------------------- 1.8.0 rights fields (docs/dev/rights-1.8.0-build.md) */

/** The model's rights fields by role (the first field with each role; rightsRoleProblems refuses a second one). */
export function rightsFields(def: { fields: FieldDefinition[] } | null | undefined): Partial<Record<RightsRole, FieldDefinition>> {
  const out: Partial<Record<RightsRole, FieldDefinition>> = {};
  for (const f of def?.fields ?? []) {
    if (f.role && (RIGHTS_ROLES as readonly string[]).includes(f.role) && !out[f.role as RightsRole]) out[f.role as RightsRole] = f;
  }
  return out;
}

/**
 * An asset's rights values by role: its own value per field, or where that is empty the value inherited from its
 * groups (as the facets treat inherited values). `inherited` is assets.inherited_metadata (field id → value) or
 * AssetSummary.inheritedMetadata (field id → InheritedValue); both are read.
 */
export function rightsValues(
  def: { fields: FieldDefinition[] } | null | undefined,
  metadata: Record<string, unknown> | null | undefined,
  inherited?: Record<string, unknown> | null,
): RightsValues {
  const out: RightsValues = {};
  for (const [role, f] of Object.entries(rightsFields(def)) as Array<[RightsRole, FieldDefinition]>) {
    const own = metadata?.[f.id];
    if (!isEmptyValue(own)) { out[role] = own; continue; }
    let inh = inherited?.[f.id];
    if (inh && typeof inh === 'object' && !Array.isArray(inh) && 'groupId' in inh && 'value' in inh) inh = (inh as { value: unknown }).value;
    if (!isEmptyValue(inh)) out[role] = inh;
  }
  return out;
}

/** The section (and, in a model with tabs, the tab) that "Add rights fields" puts the fields in. */
export const RIGHTS_SECTION = { id: 'rights', label: 'Rights', tab: 'rights' } as const;
export const RIGHTS_TAB = { id: 'rights', label: 'Rights' } as const;

/** The vocabulary of the Source field (editable once added). */
export const RIGHTS_SOURCE_OPTIONS = [
  { value: 'own_production', label: 'Own production' },
  { value: 'agency', label: 'Agency' },
  { value: 'ebu', label: 'EBU / news exchange' },
  { value: 'viewer_video', label: 'Viewer video' },
  { value: 'archive', label: 'Archive' },
  { value: 'music_library', label: 'Music library' },
  { value: 'other', label: 'Other' },
];

/** The eight fields "Add rights fields" adds (plan section 2), one per role, with stable ids. */
export const RIGHTS_FIELD_TEMPLATES: readonly FieldDefinition[] = [
  { id: 'f_rights_status', label: 'Rights status', type: 'vocabulary', required: false, role: 'rights_status', section: 'rights',
    options: RIGHTS_STATUSES.map((value) => ({ value, label: RIGHTS_STATUS_LABELS[value] })),
    help: 'Cleared, Restricted, Not cleared or Withdrawn. Withdrawn (a kill order) stops every export, share and download' },
  { id: 'f_rights_holder', label: 'Rights holder', type: 'text', required: false, role: 'rights_holder', section: 'rights', maxLength: 500,
    help: 'Who holds the rights' },
  { id: 'f_rights_source', label: 'Source', type: 'vocabulary', required: false, role: 'rights_source', section: 'rights', options: RIGHTS_SOURCE_OPTIONS,
    help: 'Where the material came from' },
  { id: 'f_rights_reference', label: 'Contract or reference', type: 'text', required: false, role: 'rights_reference', section: 'rights', maxLength: 500,
    help: 'The contract, licence or agency reference' },
  { id: 'f_rights_from', label: 'Available from', type: 'datetime', required: false, role: 'rights_from', section: 'rights',
    help: 'Not to be used before this time (an embargo or the licence start)' },
  { id: 'f_rights_until', label: 'Available until', type: 'date', required: false, role: 'rights_until', section: 'rights',
    help: 'The rights end at the end of this day, in the workspace time zone' },
  { id: 'f_rights_restrictions', label: 'Restrictions', type: 'text', required: false, role: 'rights_restrictions', section: 'rights', maxLength: 2000,
    help: 'Any restrictions, in plain words' },
  { id: 'f_rights_credit', label: 'Credit', type: 'text', required: false, role: 'rights_credit', section: 'rights', maxLength: 500,
    help: 'The credit to show with the material' },
];

/**
 * The model with the rights fields added: a field for each rights role the model has no field for yet, in the Rights
 * section (and, when the model has tabs, the Rights tab). A role already on a field keeps that field. `ids` gives a
 * template another id (role → id; Administration → Rights picks fresh ids when a template's id was used before, as
 * field ids are permanent); a template whose id is still taken by another field is left out. An existing section
 * labelled "Rights" is reused (with its tab), else an existing tab labelled "Rights" takes a new section; new
 * section and tab ids never clobber ids the model already uses. The same object when nothing is missing.
 */
export function withRightsFields(def: MetadataSchemaDefinition, ids: Partial<Record<RightsRole, string>> = {}): MetadataSchemaDefinition {
  const have = rightsFields(def);
  const taken = new Set(def.fields.map((f) => f.id));
  const add = RIGHTS_FIELD_TEMPLATES
    .filter((t) => !have[t.role as RightsRole])
    .map((t) => ({ ...t, id: ids[t.role as RightsRole] ?? t.id }))
    .filter((t) => !taken.has(t.id));
  if (!add.length) return def;
  const isRights = (label: string) => label.trim().toLowerCase() === RIGHTS_SECTION.label.toLowerCase();
  const freeId = (base: string, used: Set<string>) => {
    if (!used.has(base)) return base;
    let n = 2;
    while (used.has(`${base}_${n}`)) n++;
    return `${base}_${n}`;
  };
  const hasTabs = Boolean(def.tabs?.length);
  let tabs = def.tabs;
  let sections = def.sections;
  let sectionId: string;
  const existing = def.sections.find((s) => isRights(s.label));
  if (existing) {
    sectionId = existing.id;
  } else {
    sectionId = freeId(RIGHTS_SECTION.id, new Set(def.sections.map((s) => s.id)));
    let tabId: string | undefined;
    if (hasTabs) {
      const tab = def.tabs!.find((t) => isRights(t.label));
      if (tab) tabId = tab.id;
      else {
        tabId = freeId(RIGHTS_TAB.id, new Set(def.tabs!.map((t) => t.id)));
        tabs = [...def.tabs!, { id: tabId, label: RIGHTS_TAB.label }];
      }
    }
    sections = [...def.sections, tabId ? { id: sectionId, label: RIGHTS_SECTION.label, tab: tabId } : { id: sectionId, label: RIGHTS_SECTION.label }];
  }
  const fields = add.map((f) => ({ ...f, section: sectionId, options: f.options?.map((o) => ({ ...o })) }));
  return { ...def, ...(tabs ? { tabs } : {}), sections, fields: [...def.fields, ...fields] };
}

/**
 * The model check of rights roles (catalog/schemas.ts checkDefinition): one field per role, and each on the right
 * type. Status: a single-value vocabulary holding the four fixed values; Available from: date and time; Available
 * until: date; Source: a single-value vocabulary or text; the others: text.
 */
export function rightsRoleProblems(def: { fields: FieldDefinition[] }): string[] {
  const problems: string[] = [];
  const seen = new Map<string, FieldDefinition>();
  for (const f of def.fields) {
    if (!f.role || !(RIGHTS_ROLES as readonly string[]).includes(f.role)) continue;
    const role = f.role as RightsRole;
    const first = seen.get(role);
    if (first) problems.push(`${f.label}: “${first.label}” is already used for ${RIGHTS_ROLE_LABELS[role]}; a role can be on one field only`);
    else seen.set(role, f);
    const single = f.type === 'vocabulary' && !f.multiple;
    if (role === 'rights_status') {
      const values = new Set((f.options ?? []).map((o) => o.value));
      if (!single) problems.push(`${f.label}: the rights status must be a controlled vocabulary with one value`);
      else if (RIGHTS_STATUSES.some((s) => !values.has(s))) problems.push(`${f.label}: the rights status needs the values ${RIGHTS_STATUSES.join(', ')} (their labels can change)`);
    } else if (role === 'rights_from') {
      if (f.type !== 'datetime') problems.push(`${f.label}: Available from must be a date and time field`);
    } else if (role === 'rights_until') {
      if (f.type !== 'date') problems.push(`${f.label}: Available until must be a date field`);
    } else if (role === 'rights_source') {
      if (!single && f.type !== 'text') problems.push(`${f.label}: the rights source must be a controlled vocabulary with one value, or text`);
    } else if (f.type !== 'text') {
      problems.push(`${f.label}: ${RIGHTS_ROLE_LABELS[role]} must be a text field`);
    }
  }
  return problems;
}

/** How the field inspector's "Used for" select names each rights role. */
export const RIGHTS_ROLE_LABELS: Record<RightsRole, string> = {
  rights_status: 'Rights status', rights_holder: 'Rights holder', rights_source: 'Rights source', rights_reference: 'Rights reference',
  rights_from: 'Available from', rights_until: 'Available until', rights_restrictions: 'Rights restrictions', rights_credit: 'Credit',
};

export const MetadataSchemaDefinition = z.object({
  entityType: z.literal('asset'),
  /** Optional tabs that group sections in the asset's Metadata view. No tabs = one implicit tab. */
  tabs: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
  sections: z.array(z.object({ id: z.string(), label: z.string(), tab: z.string().optional() })),
  fields: z.array(FieldDefinitionSchema),
});
export type MetadataSchemaDefinition = z.infer<typeof MetadataSchemaDefinition>;

export interface MetadataTab { id: string; label: string }

/** The model's tabs; empty when the model has none (one implicit tab, nothing to show). */
export function metadataTabs(def: Pick<MetadataSchemaDefinition, 'tabs'> | null | undefined): MetadataTab[] {
  return def?.tabs ?? [];
}

/** The tab a section belongs to: its own `tab`, or the first tab when unset or unknown. Null when the model has no tabs. */
export function sectionTabId(def: Pick<MetadataSchemaDefinition, 'tabs'>, section: { tab?: string } | undefined): string | null {
  const tabs = metadataTabs(def);
  if (!tabs.length) return null;
  return section?.tab && tabs.some((t) => t.id === section.tab) ? section.tab : tabs[0]!.id;
}

/** The tab a field is shown in (through its section; fields without a section sit in the first one). */
export function fieldTabId(def: Pick<MetadataSchemaDefinition, 'tabs' | 'sections'>, field: { section?: string }): string | null {
  const section = def.sections.find((s) => s.id === (field.section ?? def.sections[0]?.id));
  return sectionTabId(def, section);
}

/** The model's fields in the order the form shows them: by tab, then section, then their position in the model. */
export function fieldsInModelOrder(def: Pick<MetadataSchemaDefinition, 'tabs' | 'sections' | 'fields'>): FieldDefinition[] {
  const tabs = metadataTabs(def);
  const rank = new Map(def.sections.map((s, i) => [s.id, (tabs.findIndex((t) => t.id === sectionTabId(def, s)) + 1) * 10_000 + i]));
  const first = def.sections[0]?.id;
  const at = (f: FieldDefinition) => rank.get(f.section ?? first ?? '') ?? Number.MAX_SAFE_INTEGER;
  return def.fields.map((f, i) => ({ f, i })).sort((a, b) => at(a.f) - at(b.f) || a.i - b.i).map((x) => x.f);
}

export interface MetadataSchemaDTO {
  id: string;
  version: number;
  definition: MetadataSchemaDefinition;
  publishedAt?: string;
}

/** The caller as field permissions see them: their role key and effective permissions. */
export interface FieldActor { role: string; permissions: readonly string[] }

export function canViewField(field: FieldDefinition, who: FieldActor): boolean {
  if (field.hidden) return false; // hidden for everyone until shown again in the model editor
  return !field.viewRoles?.length || field.viewRoles.includes(who.role) || who.permissions.includes('admin');
}

export function canEditField(field: FieldDefinition, who: FieldActor): boolean {
  if (!who.permissions.includes('asset.edit_metadata')) return false;
  return canViewField(field, who) && (!field.editRoles?.length || field.editRoles.includes(who.role) || who.permissions.includes('admin'));
}

/**
 * Whether the field applies to an asset with these values and this class (category id, null for none): its
 * `visibleWhen` field condition and class condition, each only when set, must both hold.
 */
export function isFieldVisible(field: FieldDefinition, values: Record<string, unknown>, categoryId: string | null): boolean {
  const w = field.visibleWhen;
  if (!w) return true;
  if (w.classes?.length && !(categoryId && w.classes.includes(categoryId))) return false;
  if (w.field) {
    const v = values[w.field];
    if (!(w.equals ?? []).some((e) => (Array.isArray(v) ? v.includes(e as string) : v === e))) return false;
  }
  return true;
}

export function isEmptyValue(raw: unknown): boolean {
  return raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '') || (Array.isArray(raw) && raw.length === 0);
}

/**
 * The fields that make an asset's metadata incomplete: visible for its class, flagged needed (or required, which wins)
 * and empty. Hidden fields never count.
 */
export function missingNeededFields(
  def: Pick<MetadataSchemaDefinition, 'fields'> | null | undefined,
  values: Record<string, unknown>,
  categoryId: string | null,
): FieldDefinition[] {
  return (def?.fields ?? []).filter((f) => (f.needed || f.required) && !f.hidden && isFieldVisible(f, values, categoryId) && isEmptyValue(values[f.id]));
}

/** Server-side validation of metadata values against a schema. */
export function validateMetadata(
  def: MetadataSchemaDefinition,
  values: Record<string, unknown>,
  /** The asset's class: a field restricted to other classes is not required. */
  categoryId: string | null,
): { ok: true; value: Record<string, unknown> } | { ok: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const out: Record<string, unknown> = {};
  const known = new Map(def.fields.map((f) => [f.id, f]));
  for (const key of Object.keys(values)) {
    if (!known.has(key)) errors[key] = 'Unknown field';
  }
  for (const field of def.fields) {
    const raw = values[field.id];
    const empty = isEmptyValue(raw);
    if (empty) {
      if (field.required && isFieldVisible(field, values, categoryId)) errors[field.id] = `${field.label} is required`;
      continue;
    }
    switch (field.type) {
      case 'text':
      case 'rich_text': {
        if (typeof raw !== 'string') { errors[field.id] = 'Must be text'; break; }
        const v = raw.trim();
        if (field.maxLength && v.length > field.maxLength) { errors[field.id] = `At most ${field.maxLength} characters`; break; }
        if (field.required && !v) { errors[field.id] = `${field.label} is required`; break; }
        out[field.id] = v;
        break;
      }
      case 'number':
        if (typeof raw !== 'number' || !Number.isFinite(raw)) errors[field.id] = 'Must be a number';
        else if (field.min !== undefined && raw < field.min) errors[field.id] = `At least ${field.min}`;
        else if (field.max !== undefined && raw > field.max) errors[field.id] = `At most ${field.max}`;
        else out[field.id] = raw;
        break;
      case 'boolean':
        if (typeof raw !== 'boolean') errors[field.id] = 'Must be true or false';
        else out[field.id] = raw;
        break;
      case 'date':
        if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw) || Number.isNaN(Date.parse(raw))) errors[field.id] = 'Use YYYY-MM-DD';
        else out[field.id] = raw;
        break;
      case 'datetime': {
        // Any ISO 8601 instant with Z or an offset, stored in UTC (so stored values sort and compare as text).
        const v = normalizeDateTime(raw);
        if (!v) errors[field.id] = 'Use a date and time, e.g. 2026-10-20T18:00:00+03:00';
        else out[field.id] = v;
        break;
      }
      case 'timecode':
        if (typeof raw !== 'string' || !/^\d{2}:\d{2}:\d{2}[:;]\d{2}$/.test(raw)) errors[field.id] = 'Use HH:MM:SS:FF';
        else out[field.id] = raw;
        break;
      case 'vocabulary': {
        const allowed = new Set((field.options ?? []).map((o) => o.value));
        const list = field.multiple ? raw : [raw];
        if (!Array.isArray(list) || list.some((v) => typeof v !== 'string' || !allowed.has(v))) errors[field.id] = 'Choose from the list';
        else out[field.id] = raw;
        break;
      }
      case 'tags': {
        if (!Array.isArray(raw) || raw.some((v) => typeof v !== 'string')) { errors[field.id] = 'Must be a list of tags'; break; }
        const tags = [...new Set(raw.map((t: string) => t.trim()).filter(Boolean))];
        if (tags.some((t) => t.length > 64)) { errors[field.id] = 'Tags are at most 64 characters'; break; }
        if (tags.length > 50) { errors[field.id] = 'At most 50 tags'; break; }
        out[field.id] = tags;
        break;
      }
    }
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, value: out };
}
